import React, { useState } from 'react';
import { ResultCard } from '../components/shared/ResultCard';
import { DataTable } from '../components/shared/DataTable';
import { 
  Plug, Plus, Code, Check, AlertTriangle, Play, FileCode, 
  RefreshCw, Shield, ChevronRight, Copy, Terminal 
} from 'lucide-react';
import { connectorRegistry, connectorSDKContract, generatedConnectorCode } from '../data/automationData';
import { useToast } from '../context/ToastContext';

const connectorColumns = [
  { key: 'name', label: 'Connector', render: (val) => <span className="font-medium text-black">{val}</span> },
  { key: 'version', label: 'Version', render: (val) => <code className="px-2 py-0.5 bg-gray-100 rounded text-xs">{val}</code> },
  { key: 'status', label: 'Status', render: (val) => (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
      val === 'active' ? 'bg-primary/10 text-primary' :
      val === 'beta' ? 'bg-slate-200 text-slate-700' :
      'bg-gray-100 text-gray-600'
    }`}>{val}</span>
  )},
  { key: 'lastSync', label: 'Last Sync' },
  { key: 'entities', label: 'Entities', render: (val) => val.toLocaleString() },
  { key: 'actions', label: 'Actions' }
];

export const ConnectorStudio = () => {
  const { addToast } = useToast();
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [connectorConfig, setConnectorConfig] = useState({
    name: '',
    authType: 'OAuth 2.0',
    openApiSpec: ''
  });
  const [showGeneratedCode, setShowGeneratedCode] = useState(false);

  const handleGenerate = () => {
    if (!connectorConfig.name) {
      addToast('Please enter a connector name', 'error');
      return;
    }
    setShowGeneratedCode(true);
    addToast('Connector code generated successfully', 'success');
  };

  const handleAction = (action) => {
    addToast(`Demo action: ${action}`, 'info');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-black mb-1">Connector Studio</h1>
            <p className="text-gray-500">Build, test, and deploy connectors for your systems</p>
          </div>
          <button
            onClick={() => { setShowWizard(true); setWizardStep(1); setShowGeneratedCode(false); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#4a70ff] text-white font-medium rounded-lg hover:bg-[#3a5fe6] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Generate Connector
          </button>
        </div>

        {/* Wizard Modal */}
        {showWizard && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Generate Connector</h2>
                <button onClick={() => setShowWizard(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[70vh]">
                {!showGeneratedCode ? (
                  <div className="space-y-6">
                    {/* Step Indicators */}
                    <div className="flex items-center gap-4 mb-8">
                      {[1, 2, 3].map((step) => (
                        <div key={step} className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                            wizardStep >= step ? 'bg-[#4a70ff] text-white' : 'bg-gray-200 text-gray-500'
                          }`}>
                            {step}
                          </div>
                          <span className={`text-sm ${wizardStep >= step ? 'text-black' : 'text-gray-400'}`}>
                            {step === 1 ? 'Basic Info' : step === 2 ? 'Authentication' : 'API Spec'}
                          </span>
                          {step < 3 && <ChevronRight className="w-4 h-4 text-gray-300" />}
                        </div>
                      ))}
                    </div>

                    {wizardStep === 1 && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Connector Name</label>
                          <input
                            type="text"
                            value={connectorConfig.name}
                            onChange={(e) => setConnectorConfig({ ...connectorConfig, name: e.target.value })}
                            placeholder="e.g., Acme HR System"
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a70ff]/20 focus:border-[#4a70ff]"
                          />
                        </div>
                        <button
                          onClick={() => setWizardStep(2)}
                          className="px-4 py-2 bg-[#4a70ff] text-white rounded-lg hover:bg-[#3a5fe6]"
                        >
                          Next
                        </button>
                      </div>
                    )}

                    {wizardStep === 2 && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Authentication Type</label>
                          <div className="grid grid-cols-2 gap-3">
                            {connectorSDKContract.authTypes.map((type) => (
                              <button
                                key={type}
                                onClick={() => setConnectorConfig({ ...connectorConfig, authType: type })}
                                className={`p-4 border rounded-lg text-left transition-all ${
                                  connectorConfig.authType === type
                                    ? 'border-[#4a70ff] bg-[#4a70ff]/5'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <div className="font-medium">{type}</div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {type === 'OAuth 2.0' && 'Recommended for SaaS apps'}
                                  {type === 'SAML' && 'Enterprise SSO integration'}
                                  {type === 'API Key' && 'Simple token-based auth'}
                                  {type === 'Service Principal' && 'Azure/cloud services'}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => setWizardStep(1)} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                            Back
                          </button>
                          <button onClick={() => setWizardStep(3)} className="px-4 py-2 bg-[#4a70ff] text-white rounded-lg hover:bg-[#3a5fe6]">
                            Next
                          </button>
                        </div>
                      </div>
                    )}

                    {wizardStep === 3 && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">OpenAPI Spec (Optional)</label>
                          <textarea
                            value={connectorConfig.openApiSpec}
                            onChange={(e) => setConnectorConfig({ ...connectorConfig, openApiSpec: e.target.value })}
                            placeholder="Paste your OpenAPI/Swagger spec here (optional)..."
                            rows={8}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a70ff]/20 focus:border-[#4a70ff] font-mono text-sm"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => setWizardStep(2)} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                            Back
                          </button>
                          <button onClick={handleGenerate} className="px-4 py-2 bg-[#4a70ff] text-white rounded-lg hover:bg-[#3a5fe6]">
                            Generate Connector
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Generated Code View */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm">
                        <Check className="w-4 h-4" />
                        Lint passed
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm">
                        <Check className="w-4 h-4" />
                        Tests passed (12/12)
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold">Generated Code</h3>
                        <button
                          onClick={() => handleAction('Copy code')}
                          className="text-sm text-[#4a70ff] hover:underline flex items-center gap-1"
                        >
                          <Copy className="w-3.5 h-3.5" />
                          Copy
                        </button>
                      </div>
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-sm text-gray-100 font-mono whitespace-pre">{generatedConnectorCode}</pre>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-3">Generated Components</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { name: 'Typed Client Stub', icon: Code, status: 'generated' },
                          { name: 'Auth Handler', icon: Shield, status: 'generated' },
                          { name: 'Pagination Wrapper', icon: RefreshCw, status: 'generated' },
                          { name: 'Retry Wrapper', icon: RefreshCw, status: 'generated' },
                          { name: 'Entity Normalizer', icon: FileCode, status: 'generated' },
                          { name: 'Action Wrappers', icon: Play, status: 'generated' }
                        ].map((item) => (
                          <div key={item.name} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <item.icon className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">{item.name}</span>
                            <Check className="w-4 h-4 text-primary ml-auto" />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button onClick={() => setShowWizard(false)} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                        Close
                      </button>
                      <button onClick={() => handleAction('Deploy connector')} className="px-4 py-2 bg-[#4a70ff] text-white rounded-lg hover:bg-[#3a5fe6]">
                        Deploy Connector
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Active Connectors', value: '7', icon: Plug, color: 'primary' },
            { label: 'Total Entities', value: '14,267', icon: FileCode, color: 'primary' },
            { label: 'Available Actions', value: '61', icon: Play, color: 'slate' },
            { label: 'Last Sync', value: '1 min ago', icon: RefreshCw, color: 'gray' }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  stat.color === 'primary' ? 'bg-primary/10' :
                  stat.color === 'slate' ? 'bg-slate-200' : 'bg-gray-100'
                }`}>
                  <stat.icon className={`w-4.5 h-4.5 ${
                    stat.color === 'primary' ? 'text-primary' :
                    stat.color === 'slate' ? 'text-slate-700' : 'text-gray-600'
                  }`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-black">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Connector Registry */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-black mb-4">Connector Registry</h2>
          <DataTable columns={connectorColumns} data={connectorRegistry} />
        </div>

        {/* SDK Contract */}
        <div>
          <h2 className="text-lg font-semibold text-black mb-4">Connector SDK Contract</h2>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
              <Terminal className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Required Methods</span>
            </div>
            <div className="divide-y divide-gray-100">
              {connectorSDKContract.methods.map((method, idx) => (
                <div key={idx} className="px-5 py-4">
                  <div className="flex items-center gap-3 mb-1">
                    <code className="px-2 py-1 bg-[#4a70ff]/10 text-[#4a70ff] rounded text-sm font-mono">{method.name}</code>
                    <span className="text-xs text-gray-400">returns {method.returns}</span>
                  </div>
                  <p className="text-sm text-gray-600">{method.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};